import { TrackerConfig } from "src/core/model/TrackerConfig";
import { TrackerConfiguration } from "src/core/model/TrackerConfiguration";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";

/**
 * Thread-safe (single-threaded in JS) shared state container.
 * Mirrors Android DataPayload — holds config and collected variables.
 */
export class DataPayload {
  private trackerConfig: TrackerConfig | null = null;
  private configuration: TrackerConfiguration | null = null;
  private collectedVariables = new Map<TrackerVariableType, unknown>();
  private userDefinedValues = new Map<string, unknown>();

  // --- TrackerConfig (backend) ---

  setTrackerConfig(config: TrackerConfig): void {
    this.trackerConfig = config;
  }

  getTrackerConfig(): TrackerConfig | null {
    return this.trackerConfig;
  }

  // --- TrackerConfiguration (SDK init) ---

  setConfiguration(config: TrackerConfiguration): void {
    this.configuration = config;
  }

  getConfiguration(): TrackerConfiguration | null {
    return this.configuration;
  }

  // --- Collected variables ---

  setVariable(type: TrackerVariableType, value: unknown): void {
    this.collectedVariables.set(type, value);
  }

  getVariable(type: TrackerVariableType): unknown {
    return this.collectedVariables.get(type);
  }

  getAllVariables(): Map<TrackerVariableType, unknown> {
    return new Map(this.collectedVariables);
  }

  getCollectedVariables(): Map<TrackerVariableType, unknown> {
    return new Map(this.collectedVariables);
  }

  updateVariables(variables: Map<TrackerVariableType, unknown>): void {
    for (const [key, value] of variables) {
      this.collectedVariables.set(key, value);
    }
  }

  // --- User-defined values ---

  setUserDefinedValue(key: string, value: unknown): void {
    this.userDefinedValues.set(key, value);
  }

  getUserDefinedValue(key: string): unknown {
    return this.userDefinedValues.get(key);
  }

  getUserDefinedValues(): Record<string, unknown> {
    return Object.fromEntries(this.userDefinedValues);
  }

  // --- Device ID ---

  getDeviceId(): string {
    try {
      let id = localStorage.getItem("formica_device_id");
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem("formica_device_id", id);
      }
      return id;
    } catch {
      return `anon_${Date.now()}`;
    }
  }

  // --- Cleanup ---

  clear(): void {
    this.trackerConfig = null;
    this.collectedVariables.clear();
    this.userDefinedValues.clear();
  }
}
